/**
 * CustomersPanel (Phase D1.1-A · Feb 2026)
 * ----------------------------------------
 * Customer directory with dedupe. Customers are also auto-created when
 * an Estimate is saved; this panel lets admins/reps manage the profile
 * and see the document history per customer.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { FilePreviewModal, isPreviewableFile } from "@/components/FilePreviewModal";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function CustomersPanel() {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/commercial/customers`, {
        params: q.trim() ? { q: q.trim() } : {},
        withCredentials: true,
      });
      setItems(data);
    } catch {
      toast.error(t("cust_panel_load_err"));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div data-testid="customers-panel" className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline">DOC / CUS</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            {t("cust_panel_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t("cust_panel_subtitle")}
          </p>
        </div>
        <button
          data-testid="customers-new"
          onClick={() => setShowNew(true)}
          className="font-display font-bold text-sm bg-black text-white px-5 py-3 hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
        >
          {t("cust_panel_new_btn")}
        </button>
      </header>

      <section className="bg-white border border-[var(--border)] p-3">
        <input
          data-testid="customers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("cust_panel_search_placeholder")}
          className="w-full border border-[var(--border)] px-3 py-2 text-sm"
        />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border border-[var(--border)]">
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-[var(--border)]">
            {loading && (
              <div className="p-4 text-sm text-[var(--text-secondary)]">{t("loading_ellipsis")}</div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-4 text-sm text-[var(--text-secondary)]">
                {t("cust_panel_empty")}
              </div>
            )}
            {items.map((c) => (
              <button
                key={c.id}
                data-testid={`customers-row-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left p-3 hover:bg-[var(--bg)] transition-colors ${
                  selectedId === c.id ? "bg-[var(--bg)] border-l-4 border-l-black" : ""
                }`}
              >
                <div className="text-sm font-bold truncate">{c.company}</div>
                <div className="text-xs text-[var(--text-secondary)] truncate">
                  {c.contact_person}
                  {c.phone ? ` · ${c.phone}` : ""}
                </div>
                <div className="text-xs text-[var(--text-secondary)] truncate">{c.email}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          {selectedId ? (
            <CustomerProfile id={selectedId} onChanged={load} />
          ) : (
            <div className="bg-white border border-[var(--border)] p-8 text-center text-sm text-[var(--text-secondary)]">
              {t("cust_panel_none_selected")}
            </div>
          )}
        </div>
      </section>

      {showNew && (
        <NewCustomerModal
          onClose={() => setShowNew(false)}
          onCreated={(c) => {
            setShowNew(false);
            setSelectedId(c.id);
            load();
          }}
        />
      )}
    </div>
  );
}

function CustomerProfile({ id, onChanged }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/commercial/customers/${id}`, {
        withCredentials: true,
      });
      setData(data);
      setForm({
        company: data.company || "",
        contact_person: data.contact_person || "",
        phone: data.phone || "",
        email: data.email || "",
        notes: data.notes || "",
      });
    } catch {
      toast.error(t("cust_profile_load_err"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);
  useEffect(() => {
    load();
  }, [load]);

  const [previewFile, setPreviewFile] = useState(null);

  const downloadFile = async (f) => {
    try {
      const res = await axios.get(
        `${API}/commercial/customers/${id}/client-files/${f.id}/download`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || f.name || "archivo";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("cust_profile_files_download_err"));
    }
  };

  const save = async () => {
    try {
      await axios.put(`${API}/commercial/customers/${id}`, form, {
        withCredentials: true,
      });
      toast.success(t("cust_profile_updated_ok"));
      setEdit(false);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cust_profile_save_err"));
    }
  };

  if (loading || !data) {
    return (
      <div className="bg-white border border-[var(--border)] p-6 text-sm text-[var(--text-secondary)]">
        {t("loading_ellipsis")}
      </div>
    );
  }

  return (
    <div data-testid={`customer-profile-${id}`} className="space-y-4">
      <section className="bg-white border border-[var(--border)] p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline text-[var(--text-secondary)]">{t("cust_profile_label")}</div>
            <h2 className="font-display text-xl sm:text-2xl font-black tracking-tighter">
              {data.company}
            </h2>
          </div>
          {!edit && (
            <button
              data-testid={`customer-edit-${id}`}
              onClick={() => setEdit(true)}
              className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              {t("cust_profile_edit_btn")}
            </button>
          )}
        </div>
        {!edit ? (
          <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div>
              <dt className="text-xs text-[var(--text-secondary)]">{t("cust_profile_contact")}</dt>
              <dd>{data.contact_person || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-secondary)]">{t("cust_profile_phone")}</dt>
              <dd>{data.phone || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-secondary)]">{t("cust_profile_email")}</dt>
              <dd>{data.email || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-secondary)]">{t("cust_profile_store_credit")}</dt>
              <dd
                data-testid={`customer-store-credit-${id}`}
                className={`font-mono-tech font-bold ${Number(data.store_credit_balance || 0) > 0 ? "text-emerald-700" : ""}`}
              >
                {money(data.store_credit_balance || 0)}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-xs text-[var(--text-secondary)]">{t("cust_profile_notes")}</dt>
              <dd className="whitespace-pre-wrap">{data.notes || "—"}</dd>
            </div>
          </dl>
        ) : (
          <div className="mt-4 space-y-3">
            {["company", "contact_person", "phone", "email"].map((k) => (
              <div key={k}>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">
                  {k.replace("_", " ")}
                </label>
                <input
                  data-testid={`customer-edit-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">{t("cust_profile_notes_lower")}</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEdit(false)}
                className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5"
              >
                {t("cancel")}
              </button>
              <button
                data-testid={`customer-save-${id}`}
                onClick={save}
                className="font-mono-tech text-xs font-bold bg-black text-white px-3 py-1.5"
              >
                {t("save")}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white border border-[var(--border)] p-5">
        <div className="label-overline text-[var(--text-secondary)] mb-3">
          {t("cust_profile_history_title")} ({(data.estimates || []).length})
        </div>
        <div className="divide-y divide-[var(--border)]">
          {(data.estimates || []).map((e) => (
            <div key={e.id} className="py-2 flex items-center justify-between text-sm">
              <div>
                <span className="font-mono-tech font-bold">{e.number}</span>{" "}
                <span className="text-xs text-[var(--text-secondary)]">
                  · {String(e.created_at || "").slice(0, 10)} · {e.status}
                </span>
              </div>
              <div className="font-mono-tech">{money(e.total_amount)}</div>
            </div>
          ))}
          {(!data.estimates || data.estimates.length === 0) && (
            <div className="py-4 text-sm text-[var(--text-secondary)]">
              {t("cust_profile_history_empty")}
            </div>
          )}
        </div>
      </section>

      <section
        data-testid={`customer-files-${id}`}
        className="bg-white border border-[var(--border)] p-5"
      >
        {previewFile && (
          <FilePreviewModal
            file={previewFile}
            downloadUrl={`${API}/commercial/customers/${id}/client-files/${previewFile.id}/download`}
            onClose={() => setPreviewFile(null)}
          />
        )}
        <div className="label-overline text-[var(--text-secondary)] mb-3">
          {t("cust_profile_files_title")} ({(data.client_files || []).length})
        </div>
        <div className="divide-y divide-[var(--border)]">
          {(data.client_files || []).map((f) => (
            <div
              key={f.id}
              data-testid={`customer-file-row-${f.id}`}
              className="py-2 flex items-center justify-between gap-3 text-sm"
            >
              <div className="min-w-0">
                <div className="font-bold truncate">{f.name || f.original_filename}</div>
                <div className="text-xs text-[var(--text-secondary)] truncate">
                  {f.original_filename}
                  {f.uploaded_at ? ` · ${String(f.uploaded_at).slice(0, 10)}` : ""}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setPreviewFile(f)}
                  data-testid={`customer-file-view-${f.id}`}
                  className="font-mono-tech text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
                  style={{ display: isPreviewableFile(f) ? undefined : "none" }}
                >
                  {t("cust_profile_files_view")}
                </button>
                <button
                  onClick={() => downloadFile(f)}
                  data-testid={`customer-file-download-${f.id}`}
                  className="font-mono-tech text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
                >
                  {t("cust_profile_files_download")}
                </button>
              </div>
            </div>
          ))}
          {(!data.client_files || data.client_files.length === 0) && (
            <div className="py-4 text-sm text-[var(--text-secondary)]">
              {t("cust_profile_files_empty")}
            </div>
          )}
        </div>
      </section>

      {Array.isArray(data.store_credit_history) && data.store_credit_history.length > 0 && (
        <section
          data-testid={`customer-sc-history-${id}`}
          className="bg-white border border-[var(--border)] p-5"
        >
          <div className="label-overline text-[var(--text-secondary)] mb-3">
            {t("cust_profile_sc_history_title")} ({data.store_credit_history.length})
          </div>
          <div className="divide-y divide-[var(--border)]">
            {data.store_credit_history
              .slice()
              .reverse()
              .map((h) => (
                <div key={h.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <span
                      className={`font-mono-tech text-[0.65rem] font-bold tracking-widest px-1.5 py-0.5 mr-2 ${
                        h.type === "applied"
                          ? "bg-neutral-800 text-white"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {h.type === "applied" ? t("cust_profile_sc_applied") : t("cust_profile_sc_issued")}
                    </span>
                    <span className="font-mono-tech font-bold">{h.source_number}</span>{" "}
                    <span className="text-xs text-[var(--text-secondary)]">
                      · {String(h.at || "").slice(0, 10)} · {h.by_name}
                    </span>
                  </div>
                  <div
                    className={`font-mono-tech font-bold ${
                      h.type === "applied" ? "text-neutral-700" : "text-emerald-700"
                    }`}
                  >
                    {h.type === "applied" ? "−" : "+"}
                    {money(h.amount)}
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}
    </div>
  );
}

function NewCustomerModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    company: "",
    contact_person: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.company.trim()) {
      toast.error(t("cust_new_company_required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/commercial/customers`, form, {
        withCredentials: true,
      });
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cust_new_create_err"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="customers-new-modal"
        className="bg-white w-full max-w-md border border-black shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="label-overline">CUS / NEW</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1 mb-4">
          {t("cust_new_title")}
        </h2>
        <div className="space-y-3">
          {["company", "contact_person", "phone", "email"].map((k) => (
            <input
              key={k}
              data-testid={`customers-new-${k}`}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              placeholder={k.replace("_", " ")}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          ))}
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t("cust_new_notes_placeholder")}
            rows={2}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5"
          >
            {t("cancel")}
          </button>
          <button
            data-testid="customers-new-save"
            disabled={saving}
            onClick={save}
            className="font-mono-tech text-xs font-bold bg-black text-white px-3 py-1.5 disabled:opacity-40"
          >
            {saving ? t("cust_new_creating") : t("cust_new_create_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}
